import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "./UserDashboard.css";

function UserDashboard() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);

  const [userProfile, setUserProfile] = useState(null);
  const [bills, setBills] = useState({ pending: [], paid: [] });
  const [loading, setLoading] = useState(true);
  const [billsLoading, setBillsLoading] = useState(false);
  const [error, setError] = useState("");

  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://backend.kplrp.in";

  const fetchUserBills = async (residentName, quarterId = null) => {
    try {
      setBillsLoading(true);

      const billsResponse = await fetch(`${API_BASE_URL}/api/bills/all`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!billsResponse.ok) {
        setBills({ pending: [], paid: [] });
        return;
      }

      const allBills = await billsResponse.json();
      let userBills = [];

      if (quarterId) {
        userBills = allBills.filter((bill) => bill.quarter_id === quarterId);
      } else {
        const quartersResponse = await fetch(`${API_BASE_URL}/api/quarters`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (quartersResponse.ok) {
          const allQuarters = await quartersResponse.json();

          const userQuarter = allQuarters.find(
            (quarter) =>
              quarter.name &&
              quarter.name.toLowerCase().trim() ===
                residentName.toLowerCase().trim()
          );

          if (userQuarter) {
            userBills = allBills.filter(
              (bill) => bill.quarter_id === userQuarter.id
            );
          }
        }
      }

      const sortedBills = userBills.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setBills({
        pending: sortedBills.filter(
          (bill) => bill.status === "pending" || bill.status === "unpaid"
        ),
        paid: sortedBills.filter((bill) => bill.status === "paid"),
      });
    } catch (err) {
      console.error("Error fetching bills:", err);
      setBills({ pending: [], paid: [] });
    } finally {
      setBillsLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user || !token) {
        setError("No authentication found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const actualName = user.username;

        const quartersResponse = await fetch(`${API_BASE_URL}/api/quarters`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!quartersResponse.ok) throw new Error("Failed to fetch quarters");

        const allQuarters = await quartersResponse.json();

        const matchingQuarter = allQuarters.find(
          (quarter) =>
            quarter.name &&
            quarter.name.toLowerCase().trim() ===
              actualName.toLowerCase().trim()
        );

        if (matchingQuarter) {
          const profile = {
            quarterId: matchingQuarter.id,
            quarterNumber: matchingQuarter.quarter_no,
            name: matchingQuarter.name,
            pnoNo: matchingQuarter.pno_no,
            rank: matchingQuarter.rank,
            mobile: matchingQuarter.mobile_no || "Not Updated",
            quarterType: matchingQuarter.type,
            block: matchingQuarter.block,
            allotmentDate: matchingQuarter.allotment_date
              ? new Date(matchingQuarter.allotment_date).toLocaleDateString(
                  "en-GB"
                )
              : "Not Available",
            hasQuarterAssignment: true,
          };

          setUserProfile(profile);
          await fetchUserBills(matchingQuarter.name, matchingQuarter.id);
        } else {
          setUserProfile({
            quarterNumber: "Not Assigned",
            name: actualName,
            hasQuarterAssignment: false,
          });
        }
      } catch (err) {
        console.error(err);
        setError("Unable to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user, token, API_BASE_URL]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  const formatAmount = (amount) => {
    return `₹${amount ? amount.toLocaleString("en-IN") : "0"}`;
  };

  if (loading) {
    return (
      <div className="user-dashboard">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <h1>Welcome, {userProfile?.name}</h1>
        <h2>USER DASHBOARD</h2>
      </div>

      {error && <div className="error-notice">{error}</div>}

      <div className="dashboard-sections">
        <div className="card">
          <h3>My Profile</h3>
          {userProfile && (
            <ul>
              <li>Quarter Number: {userProfile.quarterNumber}</li>
              <li>Block: {userProfile.block}</li>
              <li>Name: {userProfile.name}</li>
              <li>PNO No: {userProfile.pnoNo}</li>
              <li>Rank: {userProfile.rank}</li>
              <li>Mobile: {userProfile.mobile}</li>
              <li>Quarter Type: {userProfile.quarterType}</li>
              <li>Allotment Date: {userProfile.allotmentDate}</li>
            </ul>
          )}
        </div>

        <div className="card">
          <h3>My Bills</h3>

          {billsLoading ? (
            <p>Loading bills...</p>
          ) : (
            <>
              <h4>Pending Bills ({bills.pending.length})</h4>

              {bills.pending.map((bill) => (
                <div key={bill.id} className="bill-item">
                  <b>{bill.bill_type}</b> - {formatAmount(bill.amount)}
                  <br />
                  Period: {bill.period}
                </div>
              ))}

              <h4>Payment History ({bills.paid.length})</h4>

              {bills.paid.map((bill) => (
                <div key={bill.id} className="bill-item">
                  <b>{bill.bill_type}</b> - {formatAmount(bill.amount)}
                  <br />
                  Paid: {formatDate(bill.updated_at)}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="card">
          <h3>Requests Portal</h3>
          <button onClick={() => navigate("/Requests")}>
            Go to Requests Portal
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;